import { useEffect } from 'react';

import { DateTime } from 'luxon';
import { FormikValues, useFormik } from 'formik';

import {
  AllowanceEnums,
  AllowanceTypesToHandle,
  AllowancesNames,
  ContractTypes,
  ProfessionalReplacementTypes,
  TravelAllowanceEnums,
  TypeOfUser,
  calendarDate,
  createWorkshift,
  createWorkshiftContract,
  getHourlyRate,
  getPharmacyCalendar,
  getRecurrence,
  getTypeOfUser,
  pharmacyActions,
  reset,
  resetLongTermContractAttributes,
  resetStatus,
  successOrSelector,
} from '@pharmaplan/common';
import { handleWorkshiftToggles } from '@pharmaplan/common/helpers/utils';
import {
  resetDrawer,
  setButtons,
  setPagination,
} from '../../actions/drawerActions';
import { makeReqParams, setHour } from '../../helpers/workshift/functions';
import { DurationTypes } from '../../helpers/Constants';
import strings from '../../localization';

import { showSuccess } from '../../components/Admin/Profile/Pharmacist/PharmacistViewProfile/helper';
import useGetChangedContract from '../LongTermContract/useGetChangedContract';
import { SelfWorkshiftValidator } from '../../helpers/commonValidators';
import {
  parseEmptyStringProxy,
  parseFloatProxy,
} from '../../helpers/proxyHandlers';
import useSaveAndRemain from '../useSaveAndCreateAnother';
import { useAppSelector } from '../useAppSelector';
import { useAppDispatch } from '../useAppDispatch';
import useRecurrence from '../useRecurrence';

import useSyncWorkshiftValues from './useSyncWorkshiftValues';
import useSyncMultiHours from './useSyncMultiHours';

const {
  travelAllowanceType,
  accommodationAllowanceType,
  paidHourlyRate,
  weekDayPaidHourlyRate,
  weekendPaidHourlyRate,
  accommodationAllowance,
  mealAllowance,
  travelAllowanceAmount,
  travelAllowanceRatePerKm,
  emergencyFees,
  otherAllowance,
} = AllowancesNames;

const { accommodation } = AllowanceEnums;

const { pharmacy } = TypeOfUser;
const { notRepeat } = DurationTypes;

const todaysISO = DateTime.now().toISO({ includeOffset: false }) ?? '';

const useAddWorkshift = (date: string | null) => {
  const dispatch = useAppDispatch();
  const { saveAndRemain, handleSetSave, resetSaveAndRemain } = useSaveAndRemain();
  const { getChangedItems, comparator } = useGetChangedContract();

  const recurrence = useAppSelector(getRecurrence);
  const cDate = useAppSelector(calendarDate);
  const userType = useAppSelector(getTypeOfUser);
  const createSuccess = useAppSelector((state) =>
    successOrSelector(
      [
        pharmacyActions.createWorkshift,
        pharmacyActions.createWorkshiftContract,
      ],
      state,
    ));

  const luxonDate = DateTime.fromISO(date || todaysISO);
  const parsedDate = date ? DateTime.fromISO(date) : null;

  const isPharmacy = userType === pharmacy;

  const onSubmit = (values: FormikValues) => {
    const val = {
      travelAllowanceRatePerKm: handleWorkshiftToggles(
        values[travelAllowanceType]?.includes(TravelAllowanceEnums.valueKm),
        values[travelAllowanceRatePerKm],
        null,
      ),
      travelAllowanceAmount: handleWorkshiftToggles(
        values[travelAllowanceType].includes(TravelAllowanceEnums.travelAmount),
        values[travelAllowanceAmount],
        null,
      ),
      mealAllowance: values[mealAllowance],
      accommodationAllowance: handleWorkshiftToggles(
        values[accommodationAllowanceType]
          === AllowanceTypesToHandle.paidValuePerNight,
        values[accommodationAllowance],
        null,
      ),
      emergencyFees: values[emergencyFees],
      otherAllowance: values[otherAllowance],
    };

    const handledForDecimals = new Proxy(val, parseFloatProxy);

    if (values?.contractType === ContractTypes.longTerm) {
      const { startDate, endDate } = comparator();
      const rates = {
        [weekendPaidHourlyRate]: parseFloat(values[weekendPaidHourlyRate]),
        [weekDayPaidHourlyRate]: parseFloat(values[weekDayPaidHourlyRate]),
      };

      const handledForEmptyString = new Proxy(rates, parseEmptyStringProxy);

      const dateParams = {
        startDate,
        endDate,
      };

      console.log({
        allowanceNotes: values?.allowanceNotes,
        isLongTermContract: values?.contractType === ContractTypes.longTerm,
        shiftContractAttributes: getChangedItems(),
        typeReplacement: values?.replacementType,
        ...makeReqParams(values, recurrence, dateParams),
        ...handledForDecimals,
        ...handledForEmptyString,
      });

      dispatch(
        createWorkshiftContract({
          allowanceNotes: values?.allowanceNotes,
          isLongTermContract: values?.contractType === ContractTypes.longTerm,
          shiftContractAttributes: getChangedItems(),
          typeReplacement: values?.replacementType,
          ...makeReqParams(values, recurrence, dateParams),
          ...handledForDecimals,
          ...handledForEmptyString,
        }),
      );

      return;
    }

    // NOTE: THE ORDER MATTERS
    dispatch(
      createWorkshift({
        typeReplacement: values?.replacementType,
        travelAllowanceTypes: values[travelAllowanceType],
        accommodationAllowanceType: handleWorkshiftToggles(
          values[accommodation],
          values[accommodationAllowanceType],
          null,
        ),
        paidHourlyRate: values[paidHourlyRate],
        ...makeReqParams(values, recurrence),
        ...handledForDecimals,
      }),
    );
  };

  const formik = useFormik({
    initialValues: {
      datePicker: parsedDate,
      startDate: null,
      endDate: null,
      selectedDate: luxonDate,
      allDay: false,
      startTime: null,
      endTime: null,
      notes: '',
      recurrence: notRepeat,
      contractType: ContractTypes.shortTerm,
      shiftContractAttributes: [],
      allowanceNotes: '',

      [travelAllowanceType]: [],
      [accommodationAllowanceType]: null,

      [weekDayPaidHourlyRate]: '',
      [weekendPaidHourlyRate]: '',
      [paidHourlyRate]: '',
      [travelAllowanceRatePerKm]: '',
      [travelAllowanceAmount]: '',
      [accommodationAllowance]: '',
      [mealAllowance]: null,
      [emergencyFees]: null,
      [otherAllowance]: null,
      weekdayHours: 0,
      weekendHours: 0,
      replacementType: ProfessionalReplacementTypes.pharmacist,
    },
    onSubmit,
    validationSchema: SelfWorkshiftValidator(),
  });

  const { setFieldValue, handleSubmit } = formik ?? {};

  useRecurrence(formik);

  const handleReset = () => {
    dispatch(reset());
    if (saveAndRemain) {
      formik.resetForm();
      dispatch(resetLongTermContractAttributes());
      formik.setFieldValue('selectedDate', luxonDate);
      showSuccess(dispatch, strings.workshiftCreatedSuccessfully);
      resetSaveAndRemain();
      return;
    }
    dispatch(resetDrawer());
  };

  const setUserWorkTimings = () => {
    setFieldValue('startTime', setHour(luxonDate, 8));
    setFieldValue('endTime', setHour(luxonDate, 22));
  };

  useEffect(() => {
    isPharmacy && dispatch(getHourlyRate());
    dispatch(setPagination({ show: false }));
    dispatch(
      setButtons({
        show: true,
        primaryButton: {
          label: strings.createWorkshift,
          onClick: handleSubmit,
        },
        secondaryButton: {
          label: strings.saveAndCreate,
          onClick: handleSetSave(handleSubmit),
        },
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (createSuccess) {
      dispatch(getPharmacyCalendar(cDate));
      handleReset();
      dispatch(
        resetStatus([
          pharmacyActions.createWorkshift,
          pharmacyActions.createWorkshiftContract,
        ]),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createSuccess]);

  useEffect(() => {
    if (formik.values.datePicker) {
      formik.setFieldValue('selectedDate', formik.values.datePicker);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.datePicker]);

  useSyncMultiHours(formik);
  useSyncWorkshiftValues(formik);

  return { setUserWorkTimings, formik };
};

export default useAddWorkshift;
